import { usePage } from '@inertiajs/vue3';
import { PageProps } from '@/app/types';
import { AuthGuardEnum } from '@/enums/AuthGuardEnum';
import { isNil } from 'lodash';
import { computed, Ref, unref } from 'vue';

/** 로그인한 유저의 정보를 반환함. */
export function getUser() {
    return usePage<PageProps>().props.auth.user;
}

const championStyleCount = 5;
export function getUserCharacterIndex(studentId: number) {
    return (studentId % championStyleCount) + 1;
}

export function isTeacher() {
    return (
        usePage<PageProps>().props.auth.guard === AuthGuardEnum.TEACHER ||
        usePage<PageProps>().props.auth.guard === AuthGuardEnum.ADMIN
    );
}

export function isStudent() {
    return usePage<PageProps>().props.auth.guard === AuthGuardEnum.STUDENT;
}

export function isScreen() {
    return window.location.href.includes('screen');
}

/**
 * 아바타 코드를 숫자로 변환합니다.
 * @param codeString A~Z
 * @returns 숫자
 */
export function getAvatarNumberCode(codeString: string | null | undefined) {
    if (isNil(codeString)) {
        return 0;
    }

    const baseAsciiValue = 'A'.charCodeAt(0) - 1;
    return codeString.charCodeAt(0) - baseAsciiValue;
}

/**
 * 문제 선택 관련 유틸리티 함수 (Composable)
 * @param selectedQuestions 선택된 문제 ID 배열 ref
 * @param allQuestions 전체 문제 배열 (Ref 또는 일반 배열)
 * @returns allSelected computed와 toggleSelectAll 함수
 */
export function useQuestionSelection<T extends { id: number | string }>(
    selectedQuestions: Ref<(number | string)[]>,
    allQuestions: T[] | Ref<T[]>,
    minSelect = 1,
) {
    /** 모든 문제가 선택되었는지 확인 */
    const allSelected = computed(() => {
        const questions = unref(allQuestions);
        return selectedQuestions.value.length === questions.length;
    });

    /** 전체 선택/해제 토글 */
    function toggleSelectAll(event: Event) {
        const target = event.target as HTMLInputElement;
        const checked = target.checked;
        const questions = unref(allQuestions);

        if (checked) {
            // 체크: 모든 문제 선택
            selectedQuestions.value = questions.map((question) => question.id);
        } else {
            // 체크 해제: 최소 minSelect개는 선택되어야 하므로 앞에서부터 남김
            selectedQuestions.value = questions.slice(0, minSelect).map((question) => question.id);
        }
    }

    return {
        allSelected,
        toggleSelectAll,
    };
}
